import { useCallback, useState } from "react";
import type { MediaAsset } from "@eaub/shared";
import { useApi } from "../api/ApiProvider";
import { useAsync } from "../lib/useAsync";
import { Modal } from "./Modal";
import { Spinner } from "./Spinner";
import { ErrorBanner } from "./ErrorBanner";
import { MediaGrid } from "./MediaGrid";
import { UploadControl } from "./UploadControl";

interface MediaPickerProps {
  multiple: boolean;
  initialSelected: string[];
  onConfirm: (ids: string[]) => void;
  onClose: () => void;
}

export function MediaPicker({ multiple, initialSelected, onConfirm, onClose }: MediaPickerProps) {
  const api = useApi();
  const load = useCallback(() => api.listMedia(), [api]);
  const { data, loading, error, reload } = useAsync<MediaAsset[]>(load, [load]);
  const [selected, setSelected] = useState<string[]>(initialSelected);

  const toggle = (asset: MediaAsset) => {
    setSelected((prev) => {
      if (multiple) {
        return prev.includes(asset.id)
          ? prev.filter((id) => id !== asset.id)
          : [...prev, asset.id];
      }
      return prev.includes(asset.id) ? [] : [asset.id];
    });
  };

  return (
    <Modal
      title={multiple ? "Select media" : "Select a media asset"}
      onClose={onClose}
      wide
      footer={
        <>
          <span className="muted media-picker__count">
            {selected.length} selected
          </span>
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary" onClick={() => onConfirm(selected)}>
            Use selection
          </button>
        </>
      }
    >
      <div className="media-picker__toolbar">
        <UploadControl onUploaded={reload} label="Upload new" />
      </div>
      {loading ? <Spinner label="Loading media…" /> : null}
      {error ? (
        <ErrorBanner message={error.message} onRetry={reload} />
      ) : null}
      {data ? (
        <MediaGrid assets={data} selectedIds={selected} onToggle={toggle} />
      ) : null}
    </Modal>
  );
}
